import React, {useEffect, useRef, useState} from "react";
import {IconProp} from "@fortawesome/fontawesome-svg-core";
import {faWaveform} from "@fortawesome/pro-solid-svg-icons";
import {Styled} from "./SmartImage.styles";

type SmartImageProps = {
    src?: string
    alt?: string
    /** object-fit of the underlying <img>. */
    fit?: 'cover' | 'contain'
    objectPosition?: string
    /** Icon shown while loading / when the source is missing. */
    icon?: IconProp
    grayscale?: boolean
    zoomOnHover?: boolean
    radius?: string
    className?: string
    style?: React.CSSProperties
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
}

/**
 * Image with a shimmering placeholder, fade-in on load and optional
 * grayscale → color hover treatment. Replaces the older `Image` component for
 * every surface of the redesign.
 */
const SmartImage = (
    {
        src,
        alt = '',
        fit = 'cover',
        objectPosition = 'center',
        icon = faWaveform,
        grayscale = false,
        zoomOnHover = false,
        radius,
        className,
        style,
        onClick
    }: SmartImageProps) => {

    const imageRef = useRef<HTMLImageElement | null>(null)

    const [isLoaded, setIsLoaded] = useState<boolean>(false)

    useEffect(() => {
        setIsLoaded(false)
        // Images restored from cache can complete before React attaches onLoad.
        if (imageRef.current?.complete && imageRef.current.naturalWidth > 0) {
            setIsLoaded(true)
        }
    }, [src])

    return (
        <Styled.Holder
            className={className}
            style={style}
            $radius={radius}
            onClick={onClick}
        >
            {!!src && (
                <Styled.Img
                    ref={imageRef}
                    src={src}
                    alt={alt}
                    loading={'lazy'}
                    draggable={false}
                    $loaded={isLoaded}
                    $fit={fit}
                    $position={objectPosition}
                    $grayscale={grayscale}
                    $zoomOnHover={zoomOnHover}
                    onLoad={() => setIsLoaded(true)}
                    onError={() => setIsLoaded(false)}
                />
            )}
            <Styled.Placeholder $visible={!isLoaded}>
                <Styled.PlaceholderIcon icon={icon}/>
            </Styled.Placeholder>
        </Styled.Holder>
    )
}

export default SmartImage
